import { create } from 'zustand';
import * as storageConstants from '../Constants/storageConstant';
import { getLocalData } from '../Services/common';

const useStore = create((set, get) => ({
  masterData: null,
  systemData: null,
  status: [],
  userOptions: [],
  vehicleOptions: [],
  driverOptions: [],
  packageOptions: [],
  statusOptions: [],
  vendorOptions: [],
  gstOptions: [],
  roleOptions: [],
  vehicleTypeOptions: [],
  dispatcherOptions: [],
  isHydrated: false,

  // Hydrate store from localStorage on init
  hydrate: () => {
    const masterData = getLocalData(storageConstants.MASTER_DATA);
    const systemData = getLocalData(storageConstants.SYSTEM_DATA);
    const status = getLocalData(storageConstants.STATUS);

    set({
      masterData,
      systemData,
      status: Array.isArray(status) ? status : [],
      isHydrated: true,
    });
    
    // Auto-bootstrap options if we have data
    get().bootstrapOptions();
  },

  bootstrapOptions: () => {
    const { systemData, status } = get();
    if (!systemData) return;

    const driverOpts = Array.isArray(systemData.drivers) 
      ? systemData.drivers.map(c => ({ label: c.name, value: c.id })) 
      : [];
    
    const packageOpts = Array.isArray(systemData.packages)
      ? systemData.packages.map(p => {
          const companyStr = p.client?.name ? ` (${p.client.name})` : '';
          const vehicleStr = p.vehicle_type?.name ? ` - ${p.vehicle_type.name}` : '';
          return {
            label: `${p.name}${companyStr}${vehicleStr}`,
            value: p.id,
            fixedCharge: p.flat_rate,
            clientId: p.client_id || p.client?.id
          };
        })
      : [];

    const statusOpts = Array.isArray(status)
      ? status.map(s => ({ label: s.name, value: s.value }))
      : [];

    const vendorOpts = (systemData && Array.isArray(systemData.clients))
      ? systemData.clients.map(c => ({ label: c.name, value: c.id }))
      : [];

    const roleOpts = (systemData && Array.isArray(systemData.roles))
      ? systemData.roles.map(c => ({
          label: (c.name.replace('_', ' ').charAt(0).toUpperCase() + c.name.replace('_', ' ').slice(1)),
          value: c.id,
        }))
      : [];

    const vehicleTypeOpts = (systemData && Array.isArray(systemData.vehicleType))
      ? systemData.vehicleType.map(c => ({ label: c.name, value: c.id }))
      : [];

    set({
      driverOptions: driverOpts,
      packageOptions: packageOpts,
      statusOptions: statusOpts,
      vendorOptions: vendorOpts,
      roleOptions: roleOpts,
      vehicleTypeOptions: vehicleTypeOpts,
    });
  },

  setMasterData: (data) => set({ masterData: data }),
  setSystemData: (data) => {
    set({ systemData: data });
    get().bootstrapOptions();
  },
  setStatus: (data) => {
    set({ status: Array.isArray(data) ? data : [] });
    get().bootstrapOptions();
  },
  setUserOptions: (data) => set({ userOptions: Array.isArray(data) ? data : [] }),
  setVehicleOptions: (data) => set({ vehicleOptions: Array.isArray(data) ? data : [] }),
  setDriverOptions: (data) => set({ driverOptions: Array.isArray(data) ? data : [] }),
  setPackageOptions: (data) => set({ packageOptions: Array.isArray(data) ? data : [] }),
  setStatusOptions: (data) => set({ statusOptions: Array.isArray(data) ? data : [] }),
  setVendorOptions: (data) => set({ vendorOptions: Array.isArray(data) ? data : [] }),
  setDispatcherOptions: (data) => set({ dispatcherOptions: Array.isArray(data) ? data : [] }),

  // Actions to help with data retrieval (optional helpers)
  getCountryOptions: () => {
    const { systemData } = get();
    if (systemData?.countries) {
      return systemData.countries.map(c => ({
        label: c.value,
        value: c.key,
      }));
    }
    return [];
  },

  clearStore: () => set({ masterData: null, systemData: null, status: [], isHydrated: false }),
}));

export default useStore;
